import { ReactElement, useCallback } from 'react';
import { AxiosResultDefaultError } from '../../../../api/request';
import { AuthenticationError } from '../../../../api/requests/tuitionProcess';
import { createFetchContext } from '../../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../../components/info/DisplayError';
import Loading from '../../../../components/info/Loading';
import { getFormData, OptionsFormDataResponse } from '../api/api';
import { SelectOptionType } from '@octano/global-ui';

interface BaseLoaderDataProps {
  campusesOptions: SelectOptionType[];
  schoolsOptions: SelectOptionType[];
}

interface BaseLoaderProps {
  children: ({ data }: { data: BaseLoaderDataProps }) => ReactElement;
}

const { FetchProvider, FetchConsumer, useFetch } = createFetchContext<
  undefined,
  OptionsFormDataResponse,
  AuthenticationError | AxiosResultDefaultError
>();

export function useFormDataLoader() {
  return useFetch();
}

export const formDataLoaderConsumer = FetchConsumer;

export default function FormDataLoader({ children }: BaseLoaderProps) {
  const request = useCallback(async () => {
    return getFormData();
  }, []);

  return (
    <FetchProvider request={request} defaultQuery={undefined} fetchImmediately>
      <FetchConsumer>
        {({ loading, data, error, refresh }) => {
          if (loading) {
            return <Loading insideCard />;
          } else if (error) {
            return (
              <DisplayError
                insideCard
                textBody={error.code}
                retryAction={refresh}
              />
            );
          } else if (data) {
            return children({
              data: {
                campusesOptions: data.campuses,
                schoolsOptions: data.schools,
              },
            });
          } else {
            return null;
          }
        }}
      </FetchConsumer>
    </FetchProvider>
  );
}
